import { GestureHandlerRootView } from 'react-native-gesture-handler';
import React, { useState, useEffect } from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { StatusBar } from 'expo-status-bar';
import { View, ActivityIndicator, StyleSheet, Platform } from 'react-native';
import { AuthProvider } from './contexts/AuthContext';
import RootNavigator from './navigation/RootNavigator';
import { navigationRef } from './navigation/navigationRef';
import AdminStack from './admin/navigation/AdminStack';
import { loadFonts } from './theme/fonts';
import { CartProvider } from './context/CartContext';
import { SocketProvider } from './contexts/SocketContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LiveSessionProvider } from './contexts/LiveSessionContext';
import GlobalLivePip from './components/mentor/GlobalLivePip';

const queryClient = new QueryClient();

import { getColor } from './theme/useTheme';

// Emergency global fallback to track "Property 'getShadow' doesn't exist"
if (typeof global.getShadow === 'undefined') {
  global.getShadow = (size) => {
    console.warn(`[Global Fallback] getShadow called for ${size} without local import!`);
    return {};
  };
}

const Stack = createStackNavigator();

import { useInAppUpdate } from './hooks/useInAppUpdate';

export default function App() {
  useInAppUpdate();
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [isAdmin] = useState(
    Platform.OS === 'web' && window.location.pathname.startsWith('/admin')
  );

  useEffect(() => {
    async function prepare() {
      try {
        await loadFonts();
        setFontsLoaded(true);
      } catch (e) {
        console.warn('Error loading fonts:', e);
      }
    }
    prepare();
  }, []);

  if (!fontsLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={getColor('primary.main')} />
      </View>
    );
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <StatusBar style="auto" />
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <CartProvider>
              <SocketProvider>
                <NotificationProvider>
                  <LiveSessionProvider>
                    <NavigationContainer ref={navigationRef}>
                      <Stack.Navigator screenOptions={{ headerShown: false }}>
                        {isAdmin ? (
                          <Stack.Screen name="Admin" component={AdminStack} />
                        ) : (
                          <Stack.Screen name="Root" component={RootNavigator} />
                        )}
                      </Stack.Navigator>
                      {/* Global PiP overlay — floats above all screens when a session is minimized */}
                      <GlobalLivePip />
                    </NavigationContainer>
                  </LiveSessionProvider>
                </NotificationProvider>
              </SocketProvider>
            </CartProvider>
          </QueryClientProvider>
        </AuthProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: getColor('background.default'),
  },
});
